import { ApolloClient, from, HttpLink, NormalizedCacheObject, Operation, split } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { WebSocketLink } from '@apollo/client/link/ws';
import { getMainDefinition } from '@apollo/client/utilities';

import { createCache } from './test';

const cache = createCache();

const logErrorsToSentryLink = onError(({ graphQLErrors }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      // Throw the error on a separate stack so sentry logs it but code execution continues
      setTimeout(() => {
        throw new Error(
          `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(locations)}, Path: ${JSON.stringify(path)}`
        );
      }, 0);
    });
  }
});

/*
Server handles all paths under `/api/graphql` the same way.
The purpose of this is to distinguish the calls in logs and log-based metrics
*/
const getHttpLinkUri = (operation: Operation): string => `/api/graphql/${operation.operationName}`;

const httpLink = from([
  logErrorsToSentryLink,
  new HttpLink({
    uri: getHttpLinkUri,
  }),
]);

const isProd = window.location.protocol === 'https:';
const wsProtocol = isProd ? 'wss' : 'ws';
const wsPort = isProd ? '' : ':4000';
const wsUri = `${wsProtocol}://${window.location.hostname}${wsPort}/api/graphql/subscriptions`;

// Create a WebSocket link:
const wsLink = new WebSocketLink({
  uri: wsUri,
  options: {
    reconnect: true,
  },
});

const link = split(
  // split based on operation type
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
  },
  wsLink,
  httpLink
);

export const client: ApolloClient<NormalizedCacheObject> = new ApolloClient({
  cache,
  link,
});
